import { useState } from 'react';
import { Check } from 'lucide-react';
import { format } from 'date-fns';
import { DatePickerField } from '../common/DatePickerField';

export interface ContactSubmission {
  contactedByPhone: boolean;
  contactedByEmail: boolean;
  phoneDate?: string | null;
  emailDate?: string | null;
  comments: string;
  eventType?: string;
}

interface AddContactPanelProps {
  /** Optional "re: event" options to close a specific event type. */
  eventTypes?: { value: string; label: string }[];
  saving?: boolean;
  onSubmit: (s: ContactSubmission) => void | Promise<void>;
}

/**
 * Reusable contact-logging panel — same UX as the Arrears page (phone/email
 * checkboxes + date + notes), presentational with an onSubmit callback so the
 * caller wires it to logClientContact (or a *_contacts write).
 */
export function AddContactPanel({ eventTypes = [], saving = false, onSubmit }: AddContactPanelProps) {
  // Browser-LOCAL today (date-fns format uses local time) — auto-correct for
  // Sydney/Melbourne DST vs Brisbane. `toISOString()` would force UTC and roll
  // the date back a day late at night east-coast.
  const today = format(new Date(), 'yyyy-MM-dd');
  const [contactedByPhone, setPhone] = useState(true);
  const [contactedByEmail, setEmail] = useState(false);
  const [date, setDate] = useState(today);
  const [eventType, setEventType] = useState('');
  const [comments, setComments] = useState('');

  // NOTE: deliberately NOT gated on isBrokerFilteredAdmin. `logClientContact`
  // opts in with `allowBrokerFilteredAdmin: true`, so a broker-restricted
  // admin MAY log a contact for a client inside her filter; the server scopes
  // it. Disabling here would remove a capability she actually has.
  const canSubmit = (contactedByPhone || contactedByEmail) && !saving;

  const submit = () => {
    if (!canSubmit) return;
    void onSubmit({
      contactedByPhone,
      contactedByEmail,
      phoneDate: contactedByPhone ? date : null,
      emailDate: contactedByEmail ? date : null,
      comments: comments.trim(),
      eventType: eventType || undefined,
    });
    setComments('');
  };

  return (
    <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-4 space-y-3">
      <div className="flex gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <input type="checkbox" checked={contactedByPhone} onChange={(e) => setPhone(e.target.checked)} className="w-4 h-4 accent-blue-600" />
          Phone
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <input type="checkbox" checked={contactedByEmail} onChange={(e) => setEmail(e.target.checked)} className="w-4 h-4 accent-blue-600" />
          Email
        </label>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="flex-1 min-w-[150px]">
          <DatePickerField
            id="contact-date"
            label="Contact date"
            value={date}
            onChange={(v) => setDate(v || today)}
            disableFuture
            compact
          />
        </div>
        {eventTypes.length > 0 && (
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Re: event</label>
            <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
              <option value="">General contact</option>
              {eventTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">Notes</label>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="What was discussed, outcome, next step…"
          rows={3}
          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white resize-y"
        />
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-semibold disabled:opacity-40 hover:brightness-110"
      >
        <Check className="w-4 h-4" />
        {saving ? 'Saving…' : 'Log contact'}
      </button>
    </div>
  );
}
